import React, { useState, useEffect } from 'react';
import { api, ApiError } from './api';
import { htmlLogoBranding } from './branding';
import { prepararVistaPreviaTicket } from './vistaPreviaTicket';
import { calcularPromocionesOffline } from './offlinePromociones';
import { crearVentaOffline } from './offlineManager';

export default function Ventas({ usuario, activa = false }) {
  const [cajaAbierta, setCajaAbierta] = useState(null);
  const [cajaInfo, setCajaInfo] = useState(null); 
  const [productos, setProductos] = useState([]);
  const [productosCargando, setProductosCargando] = useState(false);
  const [errorProductos, setErrorProductos] = useState('');
  const [clientes, setClientes] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [carrito, setCarrito] = useState([]);
  const [cantidadesEditando, setCantidadesEditando] = useState({});
  
  const [metodoPago, setMetodoPago] = useState('Efectivo');
  const [clienteSeleccionado, setClienteSeleccionado] = useState('');
  const [pagoCombinado, setPagoCombinado] = useState(false);
  const [montosPago, setMontosPago] = useState({ Efectivo: '', Tarjeta: '', 'Mercado Pago': '', Transferencia: '', 'Cuenta Corriente': '' });
  const [importeRecibido, setImporteRecibido] = useState('');
  const [descuentoGeneral, setDescuentoGeneral] = useState('');
  const [descuentosItems, setDescuentosItems] = useState({});
  const [motivoDescuento, setMotivoDescuento] = useState('');
  const [supervisorUsuario, setSupervisorUsuario] = useState('');
  const [supervisorPin, setSupervisorPin] = useState('');

  // Modal de cobro digital (QR / Transferencia)
  const [modalCobroDigital, setModalCobroDigital] = useState(false);
  const [configNegocio, setConfigNegocio] = useState(null);
  const [esperandoConfirmacion, setEsperandoConfirmacion] = useState(false);
  // P0.8/P0.14: bloqueo de doble clic durante el cobro
  const [procesandoVenta, setProcesandoVenta] = useState(false);
  
  const [ticketCalculado, setTicketCalculado] = useState({ items: [], subtotal_original: 0, descuento_total: 0, total_final: 0 });
  const [revisionPromos, setRevisionPromos] = useState(0);
  const [promocionesOffline, setPromocionesOffline] = useState([]);
  const [online, setOnline] = useState(() => navigator.onLine);

  const [mostrarModalManual, setMostrarModalManual] = useState(false);
  const [manualDesc, setManualDesc] = useState('');
  const [manualMonto, setManualMonto] = useState('');
  const [ticketReciente, setTicketReciente] = useState(null);

  // 🚀 NUEVO: Estado para guardar el historial rápido del turno
  const [ventasTurno, setVentasTurno] = useState([]);

  useEffect(() => {
    const conectado = () => setOnline(true);
    const desconectado = () => setOnline(false);
    window.addEventListener('online', conectado);
    window.addEventListener('offline', desconectado);
    return () => { window.removeEventListener('online', conectado); window.removeEventListener('offline', desconectado); };
  }, []);

  const cargarProductos = React.useCallback(async () => {
    setProductosCargando(true);
    setErrorProductos('');
    try {
      const data = await api.get(`/api/productos?t=${Date.now()}`);
      const lista = Array.isArray(data) ? data : [];
      setProductos(lista);
      return lista;
    } catch (err) {
      console.error('Error al cargar productos', err);
      setErrorProductos(err instanceof ApiError ? err.mensaje : 'No se pudieron cargar los productos.');
      return [];
    } finally {
      setProductosCargando(false);
    }
  }, []);

  const cargarDatosBasicos = async () => {
    try {
      // Pedimos los tres recursos en paralelo: el POS arranca más rápido
      // que con tres awaits secuenciales como estaba antes.
      const [dataCaja, dataProd, dataCli, dataConfig, dataPromos] = await Promise.all([
        api.get(`/api/caja/actual?t=${Date.now()}`),
        api.get(`/api/productos?t=${Date.now()}`),
        api.get('/api/clientes'),
        api.get('/api/configuracion'),
        api.get('/api/promociones/activas'),
      ]);
      setCajaInfo(dataCaja || null);
      setCajaAbierta(Boolean(dataCaja && dataCaja.abierta && dataCaja.puede_operar));
      setProductos(Array.isArray(dataProd) ? dataProd : []);
      setClientes(Array.isArray(dataCli) ? dataCli : []);
      setConfigNegocio(dataConfig || {});
      setPromocionesOffline(Array.isArray(dataPromos) ? dataPromos : []);
      
      if (dataCaja && dataCaja.abierta) {
          cargarHistorialTurno();
      }
    } catch (err) {
      console.error("Error al cargar datos básicos", err);
      // Si falla la carga inicial dejamos cajaAbierta en false para que
      // el render muestre el cartel de "Caja cerrada" en vez de quedar
      // en el spinner infinito que tenía antes.
      setCajaAbierta(false);
    }
  };

  // 🚀 NUEVO: Función para traer el historial del turno actual
  const cargarHistorialTurno = async () => {
      try {
          const data = await api.get(`/api/ventas/turno-actual?t=${Date.now()}`);
          setVentasTurno(Array.isArray(data) ? data : []);
      } catch (e) { console.error("Error al cargar historial rápido", e); }
  };

  // Carga inicial del POS aunque permanezca oculto en segundo plano.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargarDatosBasicos(); }, []);

  // App mantiene la Terminal montada para conservar el carrito. Por eso, antes
  // los productos importados después del login no aparecían: el catálogo quedaba
  // congelado con la carga inicial. Al entrar a Ventas, refrescamos el catálogo.
  useEffect(() => {
    if (activa) {
      cargarDatosBasicos();
      setRevisionPromos((valor) => valor + 1);
    }
    // cargarDatosBasicos usa las funciones y estados actuales del componente.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activa]);

  // También actualizamos cuando Inventario informa altas, ediciones o importaciones.
  useEffect(() => {
    const onProductosActualizados = () => cargarProductos();
    window.addEventListener('productos-actualizados', onProductosActualizados);
    return () => window.removeEventListener('productos-actualizados', onProductosActualizados);
  }, [cargarProductos]);

  // Cuando se crea, activa, pausa o elimina una promoción, recalculamos el
  // carrito aunque los productos y cantidades no hayan cambiado.
  useEffect(() => {
    const onPromocionesActualizadas = () => setRevisionPromos((valor) => valor + 1);
    window.addEventListener('promociones-actualizadas', onPromocionesActualizadas);
    return () => window.removeEventListener('promociones-actualizadas', onPromocionesActualizadas);
  }, []);

  // Si se vuelve a la ventana después de trabajar en otra pestaña, refrescar.
  useEffect(() => {
    const onFocus = () => {
      if (activa) cargarProductos();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [activa, cargarProductos]);

  useEffect(() => {
    const refrescar = () => { if (activa) cargarDatosBasicos(); };
    window.addEventListener('offline-cache-updated', refrescar);
    window.addEventListener('offline-sync-finished', refrescar);
    return () => {
      window.removeEventListener('offline-cache-updated', refrescar);
      window.removeEventListener('offline-sync-finished', refrescar);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activa]);

  useEffect(() => {
    if (!carrito || carrito.length === 0) {
      setTicketCalculado({ items: [], subtotal_original: 0, descuento_total: 0, total_final: 0 });
      return;
    }
    const calcularLocal = () => setTicketCalculado(calcularPromocionesOffline(carrito, promocionesOffline));
    if (!online) { calcularLocal(); return; }
    const timer = setTimeout(async () => {
      try {
        const data = await api.post('/api/promociones/calcular', { carrito });
        if (data && Array.isArray(data.items)) setTicketCalculado(data);
      } catch (e) {
        if (e instanceof ApiError && e.status === 0) calcularLocal();
        else console.error("Error conectando con el calculador:", e);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [carrito, revisionPromos, promocionesOffline, online]);

  useEffect(() => {
    let buffer = '';
    let lastTime = Date.now();
    const handleKeyDown = (e) => {
      if (mostrarModalManual || ticketReciente || cajaAbierta === false) return;
      const currentTime = Date.now();
      if (currentTime - lastTime > 50) buffer = '';
      lastTime = currentTime;
      if (e.key === 'Enter' && buffer.length > 2) {
        e.preventDefault(); 
        if (Array.isArray(productos)) {
           const productoEscaneado = productos.find(p => String(p.codigo_barras) === buffer || (Array.isArray(p.codigos_barras) && p.codigos_barras.some(c => String(c) === buffer)));
           if (productoEscaneado) agregarAlCarrito(productoEscaneado);
        }
        setBusqueda(''); 
        buffer = ''; 
      } else if (e.key.length === 1) buffer += e.key;
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [productos, mostrarModalManual, ticketReciente, cajaAbierta]); 

  if (cajaAbierta === false) {
    const otroCajero = cajaInfo?.abierta && !cajaInfo?.puede_operar ? cajaInfo?.turno?.cajero : null;
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <div style={{ backgroundColor: otroCajero ? '#fff7ed' : '#fef2f2', padding: '50px', borderRadius: '15px', border: `2px solid ${otroCajero ? '#fdba74' : '#fecdd3'}`, textAlign: 'center', maxWidth: '560px' }}>
          <h2 style={{ color: otroCajero ? '#c2410c' : '#be123c', fontSize: '24px' }}>{otroCajero ? 'Caja asignada a otro usuario' : 'Caja Cerrada'}</h2>
          {otroCajero ? (
            <p>El turno activo pertenece a <strong>{otroCajero}</strong>. Cerrá ese turno y abrí uno con <strong>{usuario?.username}</strong> para poder vender.</p>
          ) : (
            <p>Debes abrir un turno desde el <strong>Control de Caja</strong>.</p>
          )}
        </div>
      </div>
    );
  }

  const agregarAlCarrito = (prod) => {
    setCarrito(prev => {
      const existe = prev.find(item => item.id === prod.id && !item.es_manual);
      if (existe) return prev.map(item => item.id === prod.id ? { ...item, cantidad: parseFloat(item.cantidad) + 1 } : item);
      return [...prev, { ...prod, cantidad: 1, es_manual: false }];
    });
  };

  const agregarProductoManual = () => {
    if (!manualDesc || !manualMonto) return;
    setCarrito([...carrito, { id: 'MANUAL-' + Date.now(), descripcion: manualDesc, precio_venta: parseFloat(manualMonto), cantidad: 1, es_manual: true }]);
    setMostrarModalManual(false); setManualDesc(''); setManualMonto('');
  };

  const comenzarEdicionCantidad = (index, valor) => {
    setCantidadesEditando((actual) => ({ ...actual, [index]: valor }));
  };

  const confirmarCantidad = (index, item, valorForzado = null) => {
    const valorCrudo = valorForzado ?? cantidadesEditando[index] ?? item.cantidad;
    const cantidad = Number.parseFloat(String(valorCrudo).replace(',', '.'));
    if (!Number.isFinite(cantidad) || cantidad <= 0) {
      alert('La cantidad debe ser mayor que cero.');
      setCantidadesEditando((actual) => {
        const copia = { ...actual };
        delete copia[index];
        return copia;
      });
      return;
    }
    const normalizada = item?.acepta_fraccion
      ? Math.round(cantidad * 1000) / 1000
      : Math.max(1, Math.round(cantidad));
    setCarrito((actual) => actual.map((prod, i) => i === index ? { ...prod, cantidad: normalizada } : prod));
    setCantidadesEditando((actual) => {
      const copia = { ...actual };
      delete copia[index];
      return copia;
    });
  };

  const eliminarDelCarrito = (index) => setCarrito(prev => prev.filter((_, i) => i !== index));

  const calcularTicketOperativo = () => {
    const general = Math.min(100, Math.max(0, Number.parseFloat(descuentoGeneral) || 0));
    let descuentoManual = 0;
    const items = (ticketCalculado.items || []).map((item) => {
      const pctItem = Math.min(100, Math.max(0, Number.parseFloat(descuentosItems[String(item.id)]) || 0));
      const base = Number.parseFloat(item.subtotal_final || 0);
      const descItem = Math.round(base * pctItem) / 100;
      const despuesItem = Math.max(0, base - descItem);
      const descGeneral = Math.round(despuesItem * general) / 100;
      const manual = Math.round((descItem + descGeneral) * 100) / 100;
      descuentoManual = Math.round((descuentoManual + manual) * 100) / 100;
      return { ...item, descuento_manual_preview: manual, subtotal_operativo: Math.round((base - manual) * 100) / 100 };
    });
    const totalFinal = Math.max(0, Math.round(((Number(ticketCalculado.total_final || 0)) - descuentoManual) * 100) / 100);
    return { ...ticketCalculado, items, descuento_manual_total: descuentoManual, total_final: totalFinal, descuento_total_operativo: Math.round(((Number(ticketCalculado.descuento_total || 0)) + descuentoManual) * 100) / 100 };
  };

  const ticketOperativo = calcularTicketOperativo();
  const limiteDescuentoCajero = Number.parseFloat(configNegocio?.descuento_max_cajero ?? 5) || 0;
  const generalPctOperativo = Math.min(100, Math.max(0, Number.parseFloat(descuentoGeneral) || 0));
  const descuentosEfectivosItems = Object.values(descuentosItems).map((valor) => {
    const pctItem = Math.min(100, Math.max(0, Number.parseFloat(valor) || 0));
    return 100 * (1 - (1 - pctItem / 100) * (1 - generalPctOperativo / 100));
  });
  const maxDescuentoManual = Math.max(generalPctOperativo, ...descuentosEfectivosItems, 0);

  const construirPagos = () => {
    const total = Number(ticketOperativo.total_final || 0);
    if (!pagoCombinado) {
      return [{
        metodo_pago: metodoPago,
        monto: total,
        ...(metodoPago === 'Efectivo' ? { importe_recibido: Number.parseFloat(importeRecibido || total) } : {}),
      }];
    }
    return Object.entries(montosPago)
      .map(([metodo, valor]) => ({
        metodo_pago: metodo,
        monto: Number.parseFloat(valor) || 0,
        ...(metodo === 'Efectivo' ? { importe_recibido: Number.parseFloat(importeRecibido || valor) || 0 } : {}),
      }))
      .filter((pago) => pago.monto > 0);
  };

  const confirmarVenta = async () => {
    if (carrito.length === 0) return alert("El carrito está vacío");

    // P0.8/P0.14: bloqueo de doble clic. Si ya hay una venta en curso, salir.
    if (procesandoVenta) return;

    const pagosPreparados = construirPagos();
    const tieneCuentaCorriente = pagosPreparados.some((p) => p.metodo_pago === 'Cuenta Corriente');
    if (tieneCuentaCorriente && !clienteSeleccionado) {
      return alert("⚠️ Para cobrar una parte en Cuenta Corriente tenés que seleccionar un cliente.");
    }
    const sumaPagos = Math.round(pagosPreparados.reduce((s, p) => s + p.monto, 0) * 100) / 100;
    if (Math.abs(sumaPagos - Number(ticketOperativo.total_final || 0)) > 0.009) {
      return alert(`⚠️ La suma de pagos ($${sumaPagos.toFixed(2)}) debe ser igual al total ($${Number(ticketOperativo.total_final || 0).toFixed(2)}).`);
    }
    const efectivo = pagosPreparados.find((p) => p.metodo_pago === 'Efectivo');
    if (efectivo && Number(efectivo.importe_recibido) < Number(efectivo.monto)) {
      return alert('⚠️ El importe recibido no puede ser menor que el efectivo aplicado.');
    }
    if (ticketOperativo.descuento_manual_total > 0 && motivoDescuento.trim().length < 3) {
      return alert('⚠️ Indicá el motivo del descuento manual.');
    }
    if (!online && tieneCuentaCorriente) {
      return alert('⚠️ La Cuenta Corriente requiere conexión para validar saldo, límite y vencimientos. Elegí otro medio de pago.');
    }
    if (!online && ticketOperativo.descuento_manual_total > 0) {
      return alert('⚠️ Los descuentos manuales requieren conexión para validar la autorización del supervisor.');
    }

    // P0.2/P0.3/P0.4/P0.14: el backend es la única fuente de verdad.
    // El frontend manda SOLO lo imprescindible:
    //  - productos normales: { producto_id, cantidad }   (NO precio, NO costo)
    //  - productos manuales:  { es_manual, descripcion_manual, precio_manual, cantidad }
    // NUNCA mandamos usuario_id (sale del JWT en el backend).
    const productos = carrito.map((item) => {
      if (item.es_manual) {
        return {
          es_manual: true,
          descripcion_manual: item.descripcion,
          precio_manual: parseFloat(item.precio_venta),
          cantidad: parseFloat(item.cantidad),
        };
      }
      return {
        producto_id: item.id,
        cantidad: parseFloat(item.cantidad),
      };
    });

    // P0.8: clave de idempotencia generada al confirmar. Si el navegador
    // reintenta (doble clic, retry de red), el backend devuelve la misma venta.
    const idempotencyKey =
      (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `vta-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    const ventaPayload = {
      cliente_id: clienteSeleccionado || null,
      productos,
      metodo_pago: pagoCombinado ? 'Mixto' : metodoPago,
      pagos: pagosPreparados,
      descuentos: {
        general_porcentaje: Number.parseFloat(descuentoGeneral) || 0,
        items: Object.entries(descuentosItems)
          .filter(([, pct]) => Number.parseFloat(pct) > 0)
          .map(([producto_id, porcentaje]) => ({ producto_id: Number(producto_id), porcentaje: Number.parseFloat(porcentaje) })),
        motivo: motivoDescuento,
        autorizacion: { usuario: supervisorUsuario, pin: supervisorPin },
      },
      idempotency_key: idempotencyKey,
    };

    const nombreCli = Array.isArray(clientes) ? clientes.find(c => c.id.toString() === clienteSeleccionado)?.nombre || 'Consumidor Final' : 'Consumidor Final';
    const guardarLocal = async () => {
      const registro = await crearVentaOffline({
        usuario,
        turnoId: cajaInfo?.turno?.id,
        payload: ventaPayload,
        preview: {
          items: ticketOperativo.items || [],
          subtotal_original: Number(ticketOperativo.subtotal_original || 0),
          descuento_total: Number(ticketOperativo.descuento_total_operativo || 0),
          descuento_promociones: Number(ticketCalculado.descuento_total || 0),
          descuento_manual_total: Number(ticketOperativo.descuento_manual_total || 0),
          total_final: Number(ticketOperativo.total_final || 0),
          pagos: pagosPreparados,
        },
        clientes,
      });
      setProductos((actual) => actual.map((p) => {
        const vendido = productos.filter((x) => !x.es_manual && Number(x.producto_id) === Number(p.id)).reduce((s, x) => s + Number(x.cantidad || 0), 0);
        return vendido ? { ...p, cantidad_stock: Math.round((Number(p.cantidad_stock || 0) - vendido) * 1000) / 1000 } : p;
      }));
      setTicketReciente({
        id: `OFF-${registro.secuencia}`,
        ticket_serie: 'OFF',
        ticket_numero: registro.secuencia,
        fecha: new Date(registro.creada_en),
        items: ticketOperativo.items || [],
        subtotal_original: Number(ticketOperativo.subtotal_original || 0),
        descuento_total: Number(ticketOperativo.descuento_total_operativo || 0),
        descuento_promociones: Number(ticketCalculado.descuento_total || 0),
        total_final: Number(ticketOperativo.total_final || 0),
        descuento_manual_total: Number(ticketOperativo.descuento_manual_total || 0),
        pagos: pagosPreparados,
        vuelto: efectivo ? Math.max(0, Number(efectivo.importe_recibido || 0) - Number(efectivo.monto || 0)) : 0,
        metodoPago: pagoCombinado ? 'Mixto' : metodoPago,
        cliente: nombreCli,
        pendiente_sincronizacion: true,
      });
      alert('📴 Venta guardada en este dispositivo. Se sincronizará automáticamente al recuperar internet.');
    };

    setProcesandoVenta(true);
    try {
      if (!online) {
        await guardarLocal();
        return;
      }

      const data = await api.post('/api/ventas', ventaPayload);
      if (!data.success) return alert("❌ Error al cobrar.");

      // El ticket usa los importes que devuelve el BACKEND (fuente de verdad),
      // con fallback al cálculo local solo para mostrar algo si faltara.
      setTicketReciente({
          id: data.venta_id || data.ventaId,
          ticket_serie: data.ticket_serie || '0001',
          ticket_numero: data.ticket_numero || data.venta_id || data.ventaId,
          fecha: new Date(),
          items: data.items || ticketCalculado.items || [],
          subtotal_original: data.subtotal_original ?? ticketCalculado.subtotal_original ?? 0,
          descuento_total: data.descuento_total ?? ticketCalculado.descuento_total ?? 0,
          descuento_promociones: data.descuento_promociones ?? ticketCalculado.descuento_total ?? 0,
          total_final: data.total ?? data.total_final ?? ticketOperativo.total_final ?? 0,
          descuento_manual_total: data.descuento_manual_total ?? ticketOperativo.descuento_manual_total ?? 0,
          pagos: data.pagos || pagosPreparados,
          vuelto: data.vuelto || 0,
          metodoPago: pagoCombinado ? 'Mixto' : metodoPago,
          cliente: nombreCli
      });

      cargarHistorialTurno();
      if (Array.isArray(data.advertencias_lotes) && data.advertencias_lotes.length) {
        alert(`⚠️ Advertencia de vencimiento:
${data.advertencias_lotes.join('\n')}`);
      }

    } catch (err) {
      // Si la conexión se cortó después de enviar, guardamos exactamente la
      // misma clave de idempotencia. Al sincronizar, el backend devolverá la
      // venta ya creada en vez de duplicarla.
      if (err instanceof ApiError && err.status === 0) {
        try { await guardarLocal(); } catch (localError) { alert('❌ No se pudo guardar la venta offline: ' + (localError?.message || 'error local')); }
      } else {
        alert("❌ " + (err instanceof ApiError ? err.mensaje : 'Error de red.'));
      }
    } finally {
      setProcesandoVenta(false);
    }
  };

  const limpiarPantalla = () => {
      setCarrito([]); setBusqueda(''); setClienteSeleccionado(''); setMetodoPago('Efectivo'); setTicketReciente(null);
      setPagoCombinado(false); setMontosPago({ Efectivo: '', Tarjeta: '', 'Mercado Pago': '', Transferencia: '', 'Cuenta Corriente': '' });
      setImporteRecibido(''); setDescuentoGeneral(''); setDescuentosItems({}); setMotivoDescuento(''); setSupervisorUsuario(''); setSupervisorPin('');
  };

  const imprimirTicket = async () => {
      let config = {};
      try {
        config = await api.get('/api/configuracion');
      } catch (e) {
        console.error("Error al cargar configuración para ticket", e);
      }
      const ventana = window.open('', '', 'width=760,height=900');
      if (!ventana) return alert('Permití las ventanas emergentes para ver el ticket.');
      
      ventana.document.write(`
          <html>
              <head><style>
                  @page { margin: 0; } body { font-family: 'Courier New'; font-size: 12px; margin: 0; padding: 10px; width: 58mm; color: #000; }
                  .header { text-align: center; margin-bottom: 10px; } .divisor { border-top: 1px dashed #000; margin: 5px 0; }
                  table { width: 100%; border-collapse: collapse; font-size: 11px; } th { text-align: left; }
                  .cant { width: 15%; text-align: center; } .desc { width: 55%; text-align: left; } .precio { width: 30%; text-align: right; }
                  .totales { margin-top: 10px; text-align: right; } .totales h2 { font-size: 16px; margin: 5px 0; }
              </style></head>
              <body>
                  <div class="header">
                      ${htmlLogoBranding(config, { maxWidth: 150, maxHeight: 60 })}<h1>${config.nombre_negocio || 'KIOSCO POS'}</h1>
                      <p>CUIT: ${config.cuit || '00-00000000-0'}</p>
                      <div class="divisor"></div>
                      <p>TICKET NO FISCAL${ticketReciente.pendiente_sincronizacion ? '<br/><strong>PENDIENTE DE SINCRONIZAR</strong>' : ''}<br/>Nro: ${ticketReciente.ticket_serie || '0001'}-${String(ticketReciente.ticket_numero || ticketReciente.id).padStart(8, '0')}<br/>${ticketReciente.fecha.toLocaleString()}</p>
                  </div><div class="divisor"></div>
                  <table>
                      <thead><tr><th class="cant">Cant</th><th class="desc">Detalle</th><th class="precio">Subt</th></tr></thead>
                      <tbody>
                          ${(ticketReciente.items || []).map(item => `
                              <tr>
                                  <td class="cant">${Number(item.cantidad).toLocaleString('es-AR', { maximumFractionDigits: 3 })}</td>
                                  <td class="desc">
                                    ${item.descripcion.substring(0, 15)}
                                    ${item.promocion_nombre ? `<br/><small><i>(${item.promocion_nombre})</i></small>` : ''}
                                  </td>
                                  <td class="precio">
                                     $${parseFloat(item.subtotal_original).toFixed(2)}
                                     ${item.descuento_aplicado > 0 ? `<br/><span style="color:#666">-$${parseFloat(item.descuento_aplicado).toFixed(2)}</span>` : ''}
                                  </td>
                              </tr>
                          `).join('')}
                      </tbody>
                  </table>
                  <div class="divisor"></div>
                  <div class="totales">
                      <p>Subtotal: $${parseFloat(ticketReciente.subtotal_original).toFixed(2)}</p>
                      ${ticketReciente.descuento_promociones > 0 ? `<p>Promociones: -$${parseFloat(ticketReciente.descuento_promociones).toFixed(2)}</p>` : ''}
                      ${ticketReciente.descuento_manual_total > 0 ? `<p>Descuento manual: -$${parseFloat(ticketReciente.descuento_manual_total).toFixed(2)}</p>` : ''}
                      <h2>TOTAL: $${parseFloat(ticketReciente.total_final).toFixed(2)}</h2>
                      <p>Pago: ${ticketReciente.metodoPago}${ticketReciente.vuelto > 0 ? `<br/>Vuelto: $${parseFloat(ticketReciente.vuelto).toFixed(2)}` : ''}<br/>Cliente: ${ticketReciente.cliente}</p>
                      ${ticketReciente.pendiente_sincronizacion ? '<div class="divisor"></div><p><strong>Operación guardada localmente.</strong><br/>El número definitivo y la facturación ARCA se asignarán al sincronizar.</p>' : ''}
                  </div>
              </body>
          </html>
      `);
      ventana.document.close(); ventana.focus();
      prepararVistaPreviaTicket(ventana, { alFinalizar: limpiarPantalla });
  };

  // 🚀 NUEVO: Función para reimprimir cualquier ticket del historial rápido
  const reimprimirTicketViejo = async (id) => {
      try {
          let config = {};
          try { config = await api.get('/api/configuracion'); } catch { /* ignorar: ticket se puede imprimir sin config */ }

          const dataVenta = await api.get(`/api/ventas/${id}`);
          
          const ticket = dataVenta.ticket;
          const items = dataVenta.items || [];

          const ventana = window.open('', '', 'width=760,height=900');
          if (!ventana) return alert('Permití las ventanas emergentes para ver el ticket.');
          ventana.document.write(`
              <html>
                  <head><style>
                      @page { margin: 0; } body { font-family: 'Courier New'; font-size: 12px; margin: 0; padding: 10px; width: 58mm; color: #000; }
                      .header { text-align: center; margin-bottom: 10px; } .divisor { border-top: 1px dashed #000; margin: 5px 0; }
                      table { width: 100%; border-collapse: collapse; font-size: 11px; } th { text-align: left; }
                      .cant { width: 15%; text-align: center; } .desc { width: 55%; text-align: left; } .precio { width: 30%; text-align: right; }
                      .totales { margin-top: 10px; text-align: right; } .totales h2 { font-size: 16px; margin: 5px 0; }
                  </style></head>
                  <body>
                      <div class="header">
                          ${htmlLogoBranding(config, { maxWidth: 150, maxHeight: 60 })}<h1>${config.nombre_negocio || 'KIOSCO POS'}</h1>
                          <p>CUIT: ${config.cuit || '00-00000000-0'}</p>
                          <div class="divisor"></div>
                          <p>TICKET NO FISCAL (COPIA)<br/>Nro: ${ticket.ticket_serie || '0001'}-${String(ticket.ticket_numero || ticket.id).padStart(8, '0')}<br/>${new Date(ticket.fecha).toLocaleString('es-AR')}</p>
                      </div><div class="divisor"></div>
                      <table>
                          <thead><tr><th class="cant">Cant</th><th class="desc">Detalle</th><th class="precio">Subt</th></tr></thead>
                          <tbody>
                              ${items.map(item => `
                                  <tr>
                                      <td class="cant">${Number(item.cantidad).toLocaleString('es-AR', { maximumFractionDigits: 3 })}</td>
                                      <td class="desc">
                                        ${item.descripcion || 'Producto'}
                                        ${item.promocion_nombre ? `<br/><small><i>(${item.promocion_nombre})</i></small>` : ''}
                                      </td>
                                      <td class="precio">
                                         $${parseFloat(item.precio_original * item.cantidad).toFixed(2)}
                                         ${parseFloat(item.descuento_aplicado) > 0 ? `<br/><span style="color:#666">-$${parseFloat(item.descuento_aplicado).toFixed(2)}</span>` : ''}
                                      </td>
                                  </tr>
                              `).join('')}
                          </tbody>
                      </table>
                      <div class="divisor"></div>
                      <div class="totales">
                          <p>Subtotal: $${parseFloat(ticket.subtotal_original || ticket.total).toFixed(2)}</p>
                          ${parseFloat(ticket.descuento_total || 0) - parseFloat(ticket.descuento_manual_total || 0) > 0 ? `<p style="color:#ef4444; font-weight:bold;">Ahorro Promos: -$${(parseFloat(ticket.descuento_total || 0) - parseFloat(ticket.descuento_manual_total || 0)).toFixed(2)}</p>` : ''}
                          ${parseFloat(ticket.descuento_manual_total || 0) > 0 ? `<p style="color:#ef4444; font-weight:bold;">Descuento manual: -$${parseFloat(ticket.descuento_manual_total).toFixed(2)}</p>` : ''}
                          <h2>TOTAL: $${parseFloat(ticket.total).toFixed(2)}</h2>
                          <p>Pago: ${ticket.metodo_pago || ticket.forma_pago}<br/>Cliente: ${ticket.cliente_nombre || 'Consumidor Final'}</p>
                      </div>
                  </body>
              </html>
          `);
          ventana.document.close(); ventana.focus();
          prepararVistaPreviaTicket(ventana);
      } catch (err) {
          alert("❌ " + (err instanceof ApiError ? err.mensaje : 'Error al reimprimir comprobante.'));
      }
  };
  
  const terminoBusqueda = String(busqueda || '').trim().toLocaleLowerCase('es-AR');
  const filtrados = terminoBusqueda && Array.isArray(productos)
    ? productos.filter((p) => {
        const descripcion = String(p.descripcion ?? p.nombre ?? '').toLocaleLowerCase('es-AR');
        const codigo = String(p.codigo_barras ?? '').toLocaleLowerCase('es-AR');
        const alternativos = Array.isArray(p.codigos_barras) ? p.codigos_barras.join(' ').toLocaleLowerCase('es-AR') : '';
        return descripcion.includes(terminoBusqueda) || codigo.includes(terminoBusqueda) || alternativos.includes(terminoBusqueda);
      }).slice(0, 20)
    : [];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gridTemplateRows: online ? '1fr' : 'auto 1fr', gap: '20px', height: 'calc(100vh - 80px)' }}>
      {!online && <div style={{gridColumn:'1 / -1',background:'#fff7ed',border:'1px solid #fdba74',color:'#9a3412',borderRadius:'10px',padding:'10px 14px',fontWeight:700}}>
        📴 Modo sin conexión: catálogo, precios y stock guardados. Las ventas se almacenan en este dispositivo y se sincronizan al recuperar internet. Cuenta corriente, descuentos autorizados y CAE ARCA quedan bloqueados o pendientes.
      </div>}
      {/* PANEL IZQUIERDO (Catálogo arriba, Historial de turno abajo) */}
      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '10px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        
        {/* BUSCADOR Y CATÁLOGO (Ocupa el espacio flexible de arriba) */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '8px' }}>
              <input type="text" placeholder="🔍 Buscar por descripción o código..." autoFocus value={busqueda} onChange={(e) => setBusqueda(e.target.value)} style={{ flex: '1', padding: '15px', fontSize: '18px', borderRadius: '8px', border: '2px solid #cbd5e1' }} />
              <button
                type="button"
                onClick={cargarProductos}
                disabled={productosCargando}
                title="Actualizar catálogo"
                style={{ backgroundColor: '#e2e8f0', color: '#0f172a', padding: '0 16px', borderRadius: '8px', fontWeight: 'bold', border: 'none', cursor: productosCargando ? 'default' : 'pointer' }}
              >
                {productosCargando ? '⏳' : '🔄'}
              </button>
              <button onClick={() => setMostrarModalManual(true)} style={{ backgroundColor: '#0f172a', color: 'white', padding: '0 20px', borderRadius: '8px', fontWeight: 'bold', border: 'none', cursor: 'pointer' }}>➕ Manual</button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '12px', color: errorProductos ? '#dc2626' : '#64748b' }}>
              <span>{errorProductos || `${productos.length} productos disponibles`}</span>
              {busqueda && <span>{filtrados.length} coincidencias</span>}
            </div>
            <div style={{ flex: '1', overflowY: 'auto' }}>
              {filtrados.map(p => (
                <div key={p.id} onClick={() => agregarAlCarrito(p)} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', backgroundColor: '#f8fafc', marginBottom: '6px' }}>
                  <div><strong style={{ fontSize: '15px' }}>{p.descripcion}</strong><br/><small>Stock: {(parseFloat(p.cantidad_stock) || 0).toLocaleString('es-AR', { maximumFractionDigits: 3 })} {p.unidad_medida === 'unidad' ? 'u.' : (p.unidad_medida || '')}</small></div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#10b981', textAlign: 'right' }}>${parseFloat(p.precio_venta).toFixed(2)}<br/><small style={{ color: '#64748b', fontWeight: 'normal' }}>por {p.unidad_medida === 'unidad' ? 'unidad' : (p.unidad_medida || 'unidad')}</small></div>
                </div>
              ))}
              {busqueda && filtrados.length === 0 && <p style={{color:'#94a3b8', textAlign:'center', fontSize:'14px'}}>No se encontraron productos.</p>}
            </div>
        </div>

        {/* 🚀 NUEVA SECCIÓN: HISTORIAL DE VENTAS DEL TURNO ACTIVO (Fijo abajo con altura dedicada) */}
        <div style={{ marginTop: '20px', borderTop: '2px solid #e2e8f0', paddingTop: '15px', display: 'flex', flexDirection: 'column', height: '220px', overflow: 'hidden' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#1e293b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>📋 Últimos Tickets Emitidos (Turno Abierto)</span>
            <span style={{ fontSize: '12px', backgroundColor: '#dcfce3', color: '#15803d', padding: '2px 8px', borderRadius: '10px', fontWeight: 'bold' }}>
              Emitidos: {ventasTurno.length} tck
            </span>
          </h4>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #cbd5e1' }}>
                  <th style={{ padding: '8px', textAlign: 'left', color: '#475569' }}>Ticket</th>
                  <th style={{ padding: '8px', textAlign: 'left', color: '#475569' }}>Hora</th>
                  <th style={{ padding: '8px', textAlign: 'left', color: '#475569' }}>Cliente</th>
                  <th style={{ padding: '8px', textAlign: 'right', color: '#475569' }}>Total</th>
                  <th style={{ padding: '8px', textAlign: 'center', color: '#475569' }}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {ventasTurno.map(v => (
                  <tr key={v.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px', fontWeight: 'bold', color: '#0f172a' }}>{`${v.ticket_serie || '0001'}-${String(v.ticket_numero || v.id).padStart(8, '0')}`}</td>
                    <td style={{ padding: '8px', color: '#64748b' }}>{new Date(v.fecha).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}</td>
                    <td style={{ padding: '8px', color: '#475569' }}>{v.cliente_nombre || 'Consumidor Final'}</td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold', color: '#10b981' }}>${parseFloat(v.total).toFixed(2)}</td>
                    <td style={{ padding: '8px', textAlign: 'center' }}>
                      <button onClick={() => reimprimirTicketViejo(v.id)} style={{ backgroundColor: '#0f172a', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', fontWeight: 'bold' }}>
                        🖨️ Copia
                      </button>
                    </td>
                  </tr>
                ))}
                {ventasTurno.length === 0 && (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center', color: '#94a3b8', padding: '20px' }}>No hay ventas registradas en este turno.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>

      {/* PANEL DERECHO (Carrito recalculado) */}
      <div style={{ backgroundColor: '#f8fafc', padding: '20px', borderRadius: '10px', border: '1px solid #cbd5e1', display: 'flex', flexDirection: 'column' }}>
        <h3 style={{ margin: '0 0 15px 0', borderBottom: '2px solid #e2e8f0', paddingBottom: '10px' }}>🛒 Carrito Actual</h3>
        
        <div style={{ flex: '1', overflowY: 'auto', marginBottom: '15px' }}>
          {(!ticketCalculado.items || ticketCalculado.items.length === 0) && <p style={{ color: '#94a3b8', textAlign: 'center' }}>Vacío.</p>}
          
          {(ticketCalculado.items || []).map((item, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px dashed #cbd5e1' }}>
              <div style={{ flex: '1' }}>
                <strong style={{ display: 'block', fontSize: '14px' }}>{item.descripcion}</strong>
                <small style={{ color: '#64748b' }}>${parseFloat(item.precio_original).toFixed(2)} por {item.unidad_medida === 'unidad' ? 'unidad' : item.unidad_medida}</small>
                {item.promocion_id && (
                  <span style={{ backgroundColor: '#fef08a', color: '#854d0e', fontSize: '11px', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold', marginLeft: '5px' }}>
                    ¡{item.promocion_nombre}!
                  </span>
                )}
                {!item.es_manual && (
                  <div style={{ marginTop: '5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <small style={{ color: '#64748b' }}>Desc. manual:</small>
                    <input type="number" min="0" max="100" step="0.5"
                      value={descuentosItems[String(item.id)] || ''}
                      onChange={(e) => setDescuentosItems((prev) => ({ ...prev, [String(item.id)]: e.target.value }))}
                      style={{ width: '58px', padding: '3px', border: '1px solid #cbd5e1', borderRadius: '4px' }} />
                    <small>%</small>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', alignItems: 'center' }}>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={cantidadesEditando[idx] ?? item.cantidad}
                    onChange={(e) => comenzarEdicionCantidad(idx, e.target.value)}
                    onBlur={() => confirmarCantidad(idx, item)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') e.currentTarget.blur();
                    }}
                    style={{ width: '76px', padding: '8px', textAlign: 'center', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                    title={item.acepta_fraccion ? 'Ingresá kilos o fracciones con hasta 3 decimales' : 'Este producto se vende por unidades enteras'}
                  />
                  {item.acepta_fraccion && (
                    <div style={{ display: 'flex', gap: '3px' }}>
                      {[0.25, 0.5, 0.75, 1].map((valor) => (
                        <button
                          key={valor}
                          type="button"
                          onClick={() => confirmarCantidad(idx, item, valor)}
                          style={{ padding: '2px 5px', fontSize: '10px', borderRadius: '4px', border: '1px solid #cbd5e1', backgroundColor: '#fff', cursor: 'pointer' }}
                        >
                          {valor}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                
                <span style={{ width: '90px', textAlign: 'right', display: 'flex', flexDirection: 'column' }}>
                  {item.descuento_aplicado > 0 ? (
                    <>
                      <s style={{ color: '#94a3b8', fontSize: '11px' }}>${parseFloat(item.subtotal_original).toFixed(2)}</s>
                      <span style={{ color: '#10b981', fontSize: '15px', fontWeight: 'bold' }}>${parseFloat(item.subtotal_operativo ?? item.subtotal_final).toFixed(2)}</span>
                    </>
                  ) : (
                    <span style={{ fontWeight: 'bold' }}>${parseFloat(item.subtotal_operativo ?? item.subtotal_final).toFixed(2)}</span>
                  )}
                </span>
                <button onClick={() => eliminarDelCarrito(idx)} style={{ color: '#ef4444', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '18px' }}>🗑️</button>
              </div>
            </div>
          ))}
        </div>

        {/* ZONA DE COBRO */}
        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '10px', border: '2px solid #e2e8f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
            <span style={{ color: '#64748b' }}>Subtotal Original:</span>
            <span>${parseFloat(ticketOperativo.subtotal_original || 0).toFixed(2)}</span>
          </div>
          {(ticketOperativo.descuento_total_operativo > 0) && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', color: '#ef4444' }}>
              <span>Descuentos (Ahorro):</span>
              <strong>-${parseFloat(ticketOperativo.descuento_total_operativo).toFixed(2)}</strong>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', paddingTop: '10px', borderTop: '1px solid #cbd5e1' }}>
            <span style={{ fontSize: '18px', fontWeight: 'bold' }}>TOTAL FINAL:</span>
            <span style={{ fontSize: '35px', fontWeight: 'bold', color: '#10b981' }}>${parseFloat(ticketOperativo.total_final || 0).toFixed(2)}</span>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: '8px', marginBottom: '12px', alignItems: 'center' }}>
            <label style={{ fontSize: '12px', color: '#475569', fontWeight: 'bold' }}>Descuento ticket</label>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <input type="number" min="0" max="100" step="0.5" value={descuentoGeneral}
                onChange={(e) => setDescuentoGeneral(e.target.value)} placeholder="0"
                style={{ width: '70px', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px' }} /><span>%</span>
            </div>
            {ticketOperativo.descuento_manual_total > 0 && <>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: 'bold' }}>Motivo</label>
              <input value={motivoDescuento} onChange={(e) => setMotivoDescuento(e.target.value)} placeholder="Ej.: cliente frecuente"
                style={{ padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px' }} />
            </>}
            {usuario?.rol?.toLowerCase() !== 'admin' && maxDescuentoManual > limiteDescuentoCajero && <>
              <label style={{ fontSize: '12px', color: '#b45309', fontWeight: 'bold' }}>Supervisor</label>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input value={supervisorUsuario} onChange={(e) => setSupervisorUsuario(e.target.value)} placeholder="Usuario admin" style={{ flex: 1, padding: '8px', border: '1px solid #f59e0b', borderRadius: '6px' }} />
                <input type="password" inputMode="numeric" value={supervisorPin} onChange={(e) => setSupervisorPin(e.target.value)} placeholder="PIN" style={{ width: '80px', padding: '8px', border: '1px solid #f59e0b', borderRadius: '6px' }} />
              </div>
            </>}
          </div>

          <label style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px', fontSize: '13px', fontWeight: 'bold', color: '#334155' }}>
            <input type="checkbox" checked={pagoCombinado} onChange={(e) => setPagoCombinado(e.target.checked)} /> Combinar medios de pago
          </label>

          {!pagoCombinado ? <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
            <select value={metodoPago} onChange={e => setMetodoPago(e.target.value)} style={{ flex: 1, padding: '12px', borderRadius: '5px' }}>
              <option value="Efectivo">💵 Efectivo</option>
              <option value="Mercado Pago">📱 Mercado Pago (QR)</option>
              <option value="Transferencia">🏦 Transferencia</option>
              <option value="Tarjeta">💳 Tarjeta</option>
              <option value="Cuenta Corriente" disabled={!online}>📝 Fiado (Cta. Cte.){!online ? ' — requiere conexión' : ''}</option>
            </select>
            <select value={clienteSeleccionado} onChange={e => setClienteSeleccionado(e.target.value)} style={{ flex: 1, padding: '12px', borderRadius: '5px' }}>
              <option value="">Consumidor Final</option>
              {Array.isArray(clientes) && clientes.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </div> : (
            <div style={{ marginBottom: '12px', padding: '10px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
              {Object.keys(montosPago).map((metodo) => (
                <div key={metodo} style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '8px', marginBottom: '6px', alignItems: 'center' }}>
                  <span style={{ fontSize: '12px', color: '#475569' }}>{metodo}</span>
                  <input type="number" min="0" step="0.01" value={montosPago[metodo]}
                    onChange={(e) => setMontosPago((prev) => ({ ...prev, [metodo]: e.target.value }))}
                    placeholder="$ 0,00" style={{ padding: '7px', border: '1px solid #cbd5e1', borderRadius: '5px' }} />
                </div>
              ))}
              <select value={clienteSeleccionado} onChange={e => setClienteSeleccionado(e.target.value)} style={{ width: '100%', padding: '9px', borderRadius: '5px', marginTop: '4px' }}>
                <option value="">Consumidor Final</option>
                {Array.isArray(clientes) && clientes.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
          )}

          {((!pagoCombinado && metodoPago === 'Efectivo') || (pagoCombinado && Number.parseFloat(montosPago.Efectivo) > 0)) && (
            <div style={{ marginBottom: '12px', padding: '10px', backgroundColor: '#ecfdf5', borderRadius: '8px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#065f46', marginBottom: '5px' }}>Importe recibido</label>
              <input type="number" min="0" step="0.01" value={importeRecibido} onChange={(e) => setImporteRecibido(e.target.value)}
                style={{ width: '100%', padding: '10px', fontSize: '17px', boxSizing: 'border-box', border: '1px solid #6ee7b7', borderRadius: '6px' }} />
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '6px' }}>
                {[Number(pagoCombinado ? montosPago.Efectivo : ticketOperativo.total_final || 0), 1000, 2000, 5000, 10000, 20000]
                  .filter((v, i, a) => v > 0 && a.indexOf(v) === i && v >= Number(pagoCombinado ? montosPago.Efectivo || 0 : ticketOperativo.total_final || 0))
                  .map((v) => <button key={v} type="button" onClick={() => setImporteRecibido(String(v))} style={{ padding: '5px 8px', border: '1px solid #6ee7b7', borderRadius: '5px', background: 'white', cursor: 'pointer' }}>${v.toFixed(0)}</button>)}
              </div>
              <div style={{ marginTop: '6px', fontWeight: 'bold', color: '#047857' }}>Vuelto: ${Math.max(0, (Number.parseFloat(importeRecibido) || 0) - Number(pagoCombinado ? montosPago.Efectivo || 0 : ticketOperativo.total_final || 0)).toFixed(2)}</div>
            </div>
          )}
          <button
            disabled={procesandoVenta}
            onClick={() => {
              if (carrito.length === 0) return alert("El carrito está vacío");
              if (!pagoCombinado && (metodoPago === 'Mercado Pago' || metodoPago === 'Transferencia')) {
                setModalCobroDigital(true);
              } else {
                confirmarVenta();
              }
            }}
            style={{ width: '100%', padding: '20px', backgroundColor: procesandoVenta ? '#94a3b8' : (metodoPago === 'Mercado Pago' ? '#009ee3' : metodoPago === 'Transferencia' ? '#16a34a' : '#3b82f6'), color: 'white', fontSize: '20px', fontWeight: 'bold', border: 'none', borderRadius: '8px', cursor: procesandoVenta ? 'default' : 'pointer' }}
          >
            {procesandoVenta ? '⏳ Procesando...' : (metodoPago === 'Mercado Pago' ? '📱 COBRAR CON QR' : metodoPago === 'Transferencia' ? '🏦 COBRAR POR TRANSFERENCIA' : '✅ COBRAR VENTA')}
          </button>
        </div>
      </div>

      {/* MODAL: COBRO DIGITAL (QR Mercado Pago / Transferencia) */}
      {modalCobroDigital && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 3000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', width: '90%', maxWidth: '440px', overflow: 'hidden' }}>

            {/* Header */}
            <div style={{ backgroundColor: metodoPago === 'Mercado Pago' ? '#009ee3' : '#16a34a', padding: '20px 24px', color: 'white' }}>
              <h2 style={{ margin: 0, fontSize: '20px' }}>
                {metodoPago === 'Mercado Pago' ? '📱 Pago con QR de Mercado Pago' : '🏦 Transferencia Bancaria'}
              </h2>
              <p style={{ margin: '6px 0 0 0', fontSize: '14px', opacity: 0.9 }}>
                Mostrá los datos al cliente y esperá la confirmación
              </p>
            </div>

            <div style={{ padding: '24px' }}>
              {/* Monto destacado */}
              <div style={{ textAlign: 'center', marginBottom: '20px', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '2px solid #e2e8f0' }}>
                <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Monto a cobrar</p>
                <p style={{ margin: 0, fontSize: '42px', fontWeight: '800', color: metodoPago === 'Mercado Pago' ? '#009ee3' : '#16a34a' }}>
                  ${parseFloat(ticketOperativo.total_final || 0).toFixed(2)}
                </p>
              </div>

              {/* Datos de pago */}
              {metodoPago === 'Mercado Pago' && configNegocio && (
                <div>
                  {/* QR imagen si está configurada */}
                  {configNegocio.mp_qr_imagen_url && (
                    <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                      <img
                        src={configNegocio.mp_qr_imagen_url}
                        alt="QR Mercado Pago"
                        style={{ width: '180px', height: '180px', objectFit: 'contain', border: '2px solid #e2e8f0', borderRadius: '8px' }}
                        onError={e => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {configNegocio.mp_alias && (
                      <DatoCobro
                        label="Alias MP"
                        valor={configNegocio.mp_alias}
                        color="#009ee3"
                        grande
                      />
                    )}
                    {configNegocio.mp_cbu && (
                      <DatoCobro label="CBU / CVU" valor={configNegocio.mp_cbu} color="#009ee3" />
                    )}
                    {configNegocio.mp_titular && (
                      <DatoCobro label="Titular" valor={configNegocio.mp_titular} color="#009ee3" />
                    )}
                    {!configNegocio.mp_alias && !configNegocio.mp_cbu && (
                      <div style={{ padding: '12px', backgroundColor: '#fef2f2', borderRadius: '8px', color: '#dc2626', fontSize: '13px', textAlign: 'center' }}>
                        ⚠️ Configurá el alias y CBU de Mercado Pago en <strong>Configuración</strong> para que aparezcan acá.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {metodoPago === 'Transferencia' && configNegocio && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {configNegocio.banco_alias && (
                    <DatoCobro label="Alias" valor={configNegocio.banco_alias} color="#16a34a" grande />
                  )}
                  {configNegocio.banco_cbu && (
                    <DatoCobro label="CBU / CVU" valor={configNegocio.banco_cbu} color="#16a34a" />
                  )}
                  {configNegocio.banco_titular && (
                    <DatoCobro label="Titular" valor={configNegocio.banco_titular} color="#16a34a" />
                  )}
                  {configNegocio.banco_nombre && (
                    <DatoCobro label="Banco / Billetera" valor={configNegocio.banco_nombre} color="#16a34a" />
                  )}
                  {!configNegocio.banco_alias && !configNegocio.banco_cbu && (
                    <div style={{ padding: '12px', backgroundColor: '#fef2f2', borderRadius: '8px', color: '#dc2626', fontSize: '13px', textAlign: 'center' }}>
                      ⚠️ Configurá el alias y CBU del banco en <strong>Configuración</strong> para que aparezcan acá.
                    </div>
                  )}
                </div>
              )}

              {/* Botones */}
              <div style={{ display: 'flex', gap: '10px', marginTop: '24px' }}>
                <button
                  onClick={() => { setModalCobroDigital(false); setEsperandoConfirmacion(false); }}
                  disabled={esperandoConfirmacion}
                  style={{ flex: 1, padding: '14px', backgroundColor: '#e2e8f0', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', opacity: esperandoConfirmacion ? 0.5 : 1 }}
                >
                  Cancelar
                </button>
                <button
                  onClick={async () => {
                    setEsperandoConfirmacion(true);
                    await confirmarVenta();
                    setModalCobroDigital(false);
                    setEsperandoConfirmacion(false);
                  }}
                  disabled={esperandoConfirmacion}
                  style={{ flex: 2, padding: '14px', backgroundColor: esperandoConfirmacion ? '#94a3b8' : (metodoPago === 'Mercado Pago' ? '#009ee3' : '#16a34a'), color: 'white', border: 'none', borderRadius: '8px', cursor: esperandoConfirmacion ? 'default' : 'pointer', fontWeight: 'bold', fontSize: '15px' }}
                >
                  {esperandoConfirmacion ? '⏳ Registrando...' : '✅ Confirmar pago recibido'}
                </button>
              </div>

              <p style={{ textAlign: 'center', margin: '12px 0 0 0', fontSize: '12px', color: '#94a3b8' }}>
                Solo tocá "Confirmar" cuando hayas visto la notificación de pago en tu celular
              </p>
            </div>
          </div>
        </div>
      )}

      {/* MODALES */}
      {ticketReciente && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '10px', width: '350px' }}>
            <h2 style={{ textAlign: 'center', color: '#10b981' }}>¡Venta Exitosa!</h2>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button onClick={limpiarPantalla} style={{ flex: 1, padding: '12px', backgroundColor: '#e2e8f0', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}>Cerrar</button>
              <button onClick={imprimirTicket} style={{ flex: 1, padding: '12px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}>🖨️ Imprimir Ticket</button>
            </div>
          </div>
        </div>
      )}

      {mostrarModalManual && (
         <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
         <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '10px', width: '400px' }}>
           <h3 style={{ margin: '0 0 20px 0' }}>🏷️ Producto Manual</h3>
           <input type="text" placeholder="Descripción" value={manualDesc} onChange={e => setManualDesc(e.target.value)} style={{ width: '100%', padding: '10px', marginBottom: '15px', boxSizing: 'border-box' }} autoFocus />
           <input type="number" placeholder="Precio ($)" value={manualMonto} onChange={e => setManualMonto(e.target.value)} style={{ width: '100%', padding: '10px', marginBottom: '25px', boxSizing: 'border-box' }} />
           <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
             <button onClick={() => setMostrarModalManual(false)} style={{ padding: '10px 15px' }}>Cancelar</button>
             <button onClick={agregarProductoManual} style={{ padding: '10px 20px', backgroundColor: '#10b981', color: 'white', border: 'none' }}>Agregar</button>
           </div>
         </div>
       </div>
      )}
    </div>
  );
}

// Componente auxiliar para mostrar un dato de cobro con botón de copiar
function DatoCobro({ label, valor, color, grande }) {
  const [copiado, setCopiado] = React.useState(false);

  const copiar = () => {
    navigator.clipboard.writeText(valor).then(() => {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    }).catch(() => {
      // Fallback para entornos sin clipboard API (HTTP sin HTTPS)
      const el = document.createElement('textarea');
      el.value = valor;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    });
  };

  return (
    <div style={{ backgroundColor: '#f8fafc', border: `1px solid ${color}22`, borderRadius: '8px', padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
      <div>
        <p style={{ margin: 0, fontSize: '11px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>{label}</p>
        <p style={{ margin: '3px 0 0 0', fontSize: grande ? '20px' : '14px', fontWeight: grande ? '800' : '600', color, letterSpacing: grande ? '1px' : 'normal', wordBreak: 'break-all' }}>{valor}</p>
      </div>
      <button
        onClick={copiar}
        style={{ flexShrink: 0, padding: '6px 10px', backgroundColor: copiado ? '#10b981' : '#f1f5f9', color: copiado ? 'white' : '#64748b', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' }}
      >
        {copiado ? '✓ Copiado' : '📋 Copiar'}
      </button>
    </div>
  );
}
